// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridChartUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { has, isArray, get, size, isObject, head, flow,
  keys, forIn, filter, concat, isNull, isNil, sortBy } from 'lodash/fp';

/**
 * Is a node chart
 * @param  {object}  chartNode
 * @return {boolean}
 */
function hasCharts(chartNode: any) {
  return chartNode && has(chartNode.charts) && isArray(chartNode.charts);
}

/**
 * Get size based on percent
 * @param  {number} width
 * @param  {number} height
 * @return {any}
 */
function getSizePercent(width: number, height: number) {
  return {
    width: Math.min(100, width) + '%',
    height: Math.min(100, height) + '%'
  };
}


/**
 * Get group chart info
 * @param  {any} chartNode
 * @param  {Array<any>} groupLevels
 * @return {any}
 */
function getGroupChartInfo(chartNode: any, groupLevels: Array<any>) {
  const getMaxLevels = (groupInfo: any, groupPath: Array<any>) => {
    groupPath = groupPath || [];
    const groupName = get('groupName', groupInfo);
    if (!isNil(groupName)) {
      groupPath.unshift(groupName);
    }

    if (has('linkTo', groupInfo)) {
      return getMaxLevels(groupInfo.linkTo, groupPath);
    }

    return size(groupPath);
  };

  const getGroup = (group: any, level: number) => {
    if (level < 1) {
      return group;
    }

    return getGroup(group.linkTo, level - 1);
  };

  const getChart = (chartSeries: any) => {
    if (!isObject(chartSeries)) {
      return null;
    } else if (hasCharts(chartSeries)) {
      return head(chartSeries.charts);
    } else {
      return getChart(get(flow([keys, head])(chartSeries), chartSeries));
    }
  };

  const chart = getChart(chartNode);

  if (!chart) {
    return null;
  }
  const groupInfo = get('linkTo', chart);
  const maxLevel = getMaxLevels(groupInfo);

  return getGroup(groupInfo, maxLevel - size(groupLevels) - 1);
}

/**
 * get number of charts
 * @param  {any} chartTree
 * @return {number}
 */
function getNumChart(chartTree: any) {
  if (!isObject(chartTree)) {
    return 0;
  }

  if (hasCharts(chartTree)) {
    return size(chartTree.charts);
  }

  let total = 0;

  forIn(childNode => {
    total += getNumChart(childNode);
  }, chartTree);

  return total;
}

/**
 * Build chart tree
 * @param  {Array<any>} serieCharts
 * @return {any}
 */
function buildChartTree(serieCharts: Array<any>) {
  const chartTree = {};
  const groups = {};
  let groupPath;
  let groupElement;
  const groupPathInOrder = [];

  const setData = (groupPath: Array<any>, data: any, source: any) => {
    if (!groupPath.length) {
      return;
    } else if (groupPath.length === 1) {
      source[groupPath] = data;
      return;
    } else {
      const  first = groupPath.shift();
      !source[first] && (source[first] = {});

      return setData(groupPath, data, source[first]);
    }
  };

  const getGroupName = (groupInfo: any) => {
    if (!isObject(groupInfo)) {
      return [];
    }

    const groupName = flow(
      get('linkTo.groupName'),
      groupName => isNull(groupName) ? 'null' : groupName) (groupInfo);

    if (!isNil(groupName)) {
      if (!has(groupName, groups)) {
        groups[groupName] = {
          info: groupInfo.linkTo,
          numElement: 1
        };
      } else {
        groups[groupName].numElement += 1;
      }

      const paths = getGroupName(groupInfo.linkTo);

      return concat(paths, groupName, []);
    } else {
      return [];
    }
  };

  serieCharts.forEach(chart => {
    groupPath = filter(group => group != undefined, getGroupName(chart));
    groupElement = get(groupPath, chartTree) || (has('charts', chartTree) ? chartTree : null);

    if (!groupElement) {
      groupPathInOrder.push(groupPath);
      setData(concat(groupPath, ['charts']), [chart], chartTree);
    } else {
      groupElement.charts.push(chart);
    }
  });

  return {
    chartTree: chartTree,
    groups: groups,
    groupPathInOrder: groupPathInOrder
  };
}

/**
 * Get group name in order
 * @param {any} chartTree 
 * @param {any} groupsInfo 
 * @returns {any}
 */
function getGroupNameInOrder(chartTree: any, groupsInfo: any) {
  const groupNames = keys(chartTree);

  return sortBy(name => get('info.groupIndex', groupsInfo[name]), groupNames);
}

export default {
  buildChartTree: buildChartTree,
  getNumChart: getNumChart,
  getGroupChartInfo: getGroupChartInfo,
  getSizePercent: getSizePercent,
  hasCharts: hasCharts,
  getGroupNameInOrder: getGroupNameInOrder
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.GridChartUtils.js